import type { BoundingBox } from "@/libs/cv"
import {
  oneOf,
  ThresholdsValidation,
  Validation,
  type ThresholdRanges,
  type ValidationContext,
} from "@/libs/cv/validation"
import { normalize } from "@/libs/math"
import type { Detections } from "./detector"

export type FaceValidationContext = ValidationContext<{
  face: Detections["face"]
  illumination: Detections["illumination"]
}> & {
  settings?: Partial<{ lighting: number }>
}

export const createLightningAngleDeviationValidation = (azimuthThresholds: ThresholdRanges) => [
  ThresholdsValidation<FaceValidationContext>({
    /** Light azimuth deviation in [-1, 1] range */
    value: ({ detections }) => detections.illumination?.azimuth ?? 1,
    thresholds: azimuthThresholds,
  }),
  ThresholdsValidation<FaceValidationContext>({
    /** Light elevation deviation in [-1, 1] range */
    value: ({ detections }) => detections.illumination?.elevation ?? 0,
    thresholds: {
      /** Acceptable light elevation deviation in [-1, 1] range */
      good: normalize([-30, 45], 90 /* normalization of [-90, 90] */),
      perfect: normalize([-5, 30], 90 /* normalization of [-90, 90] */),
    },
  }),
]
export const createLightingInsufficientValidation = (thresholds: ThresholdRanges) => [
  ThresholdsValidation<FaceValidationContext>({
    /** Light intensity in [0, 1] range */
    value: ({ detections }) => detections.illumination?.intensity ?? 0,
    thresholds: thresholds,
  }),
]

export const createFacePitchAngleDeviationValidation = (thresholds: ThresholdRanges) => [
  ThresholdsValidation<FaceValidationContext>({
    value: ({ detections }) => detections.face?.rotation.pitch ?? NaN,
    thresholds,
  }),
]
export const createFaceYawAngleDeviationValidation = (thresholds: ThresholdRanges) => [
  ThresholdsValidation<FaceValidationContext>({
    value: ({ detections }) => detections.face?.rotation.yaw ?? NaN,
    thresholds,
  }),
]
export const createFaceRollAngleDeviationValidation = (thresholds: ThresholdRanges) => [
  ThresholdsValidation<FaceValidationContext>({
    value: ({ detections }) => detections.face?.rotation.roll ?? NaN,
    thresholds,
  }),
]

const VISIBLE_RECT_PADDING = 0.05
export const FACE_DISPLACED = [
  Validation<FaceValidationContext, Detections["face"]>({
    value: ({ detections }) => detections.face,
    validate: (detection) => detection !== null,
  }),
  Validation<FaceValidationContext, BoundingBox | null>({
    value: ({ detections }) => (detections.face ? detections.face.boundingBox : null),
    validate: (boundingBox) =>
      boundingBox !== null &&
      boundingBox.x >= VISIBLE_RECT_PADDING &&
      boundingBox.x + boundingBox.width <= 1 - VISIBLE_RECT_PADDING &&
      boundingBox.y >= VISIBLE_RECT_PADDING &&
      boundingBox.y + boundingBox.height <= 1 - VISIBLE_RECT_PADDING,
  }),
]

export const FACE_DISTANCE_CLOSE = [
  ThresholdsValidation<FaceValidationContext>({
    /** Distance to camera in cm */
    value: ({ detections }) => (detections.face ? detections.face.distance : NaN),
    thresholds: {
      good: [25, Infinity],
      perfect: [30, Infinity],
    },
  }),
]
export const FACE_DISTANCE_FAR = [
  oneOf(
    ThresholdsValidation<FaceValidationContext>({
      /** Distance to camera in cm */
      value: ({ detections }) => (detections.face ? detections.face.distance : NaN),
      thresholds: { good: [-Infinity, 40], perfect: [-Infinity, 35] },
    }),
    /*
     * Workaround for LIQ-189:
     *
     * The distance detection algorithm depends on the camera's field of view (FoV) and is designed to work with cameras of a 63deg FoV.
     * For the most cases it works well, but some devices have cameras with a wider FoV leading to
     * the face being detected further than it actually is. An example of a such devices is OPPO Reno 10 with 89deg FoV selfie camera.
     *
     * The distance estimation algorithm references:
     * - https://github.com/google-ai-edge/mediapipe/blob/master/mediapipe/modules/face_geometry/libs/geometry_pipeline.cc#L309
     * - https://github.com/google-ai-edge/mediapipe/blob/2388822335a90fe4b9a1780f27fa16522d4fc288/mediapipe/tasks/cc/vision/face_geometry/face_geometry_from_landmarks_graph.cc#L69
     */
    ThresholdsValidation<FaceValidationContext>({
      /** Percentage of the frame filled horizontally with face */
      value: ({ detections }) => detections.face?.boundingBox.width ?? 0,
      thresholds: { good: [0.6, Infinity], perfect: [0.65, Infinity] },
    }),
    ThresholdsValidation<FaceValidationContext>({
      /** Percentage of the frame filled vertically with face */
      value: ({ detections }) => detections.face?.boundingBox.height ?? 0,
      thresholds: { good: [0.5, Infinity], perfect: [0.55, Infinity] },
    }),
  ),
]
